import ProjRef from "@/shared/components/ProjRef";
import { useEffect, useState, useCallback, useRef } from "react";
import api, { fmtErr } from "@/shared/lib/api";
import FileUpload from "@/shared/components/FileUpload";
import {
  Ruler, CheckCircle2, Clock, Send, Sparkles, ArrowLeft, ChevronRight,
  FolderOpen, ImageIcon, MessageSquare, Layers, Lock, Download, UserCheck, UserPlus,
  CheckSquare, Square, X, Pencil,
} from "lucide-react";
import { toast } from "sonner";

/* PSM Pre-Audit board (Msg 906) — review what the pre-audit team submitted for
   each wall (site measurement, photos, condition comment) and, when a wall needs
   a correction before install, send a note to the client — with or without the
   pre-audit photo. Project-grouped master-detail. */

const STATE = {
  submitted: { label: "Ready to approve",  dot: "bg-amber-400",   chip: "bg-amber-100 text-amber-800 border-amber-300" },
  approved:  { label: "Approved",          dot: "bg-emerald-500", chip: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  changes:   { label: "Sent back",         dot: "bg-rose-400",    chip: "bg-rose-100 text-rose-700 border-rose-300" },
  pending:   { label: "Awaiting pre-audit", dot: "bg-zinc-300",   chip: "bg-zinc-100 text-zinc-600 border-zinc-200" },
};
const isImg = (u) => /\.(jpe?g|png|webp|gif|svg)(\?|$)/i.test(u || "");
const lineState = (l) => {
  const s = l.preaudit_status;
  if (s === "approved") return "approved";
  if (s === "changes") return "changes";
  if (s === "submitted") return "submitted";
  return "pending";
};

export default function PreAuditReview() {
  const [projects, setProjects] = useState(null);
  const [view, setView] = useState({ code: null, bid: null });

  const load = useCallback(() => {
    api.get("/preaudit-review")
      .then((r) => setProjects(r.data.projects || []))
      .catch((e) => { toast.error(fmtErr(e.response?.data?.detail) || "Failed to load"); setProjects([]); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const active = view.code ? (projects || []).find((p) => p.project_code === view.code) : null;
  const openProject = (code) => {
    const p = (projects || []).find((x) => x.project_code === code);
    const first = p?.lines.find((l) => lineState(l) === "submitted") || p?.lines[0];
    setView({ code, bid: first?.boq_id || null });
  };

  return (
    <div className="p-5 sm:p-8 max-w-6xl mx-auto" data-testid="preaudit-review-page">
      {projects === null ? (
        <div className="py-24 text-center text-zinc-400">Loading…</div>
      ) : active ? (
        <ProjectView project={active} selectedBid={view.bid}
                     onSelect={(bid) => setView({ code: active.project_code, bid })}
                     onBack={() => setView({ code: null, bid: null })} onDone={load} />
      ) : (
        <ProjectList projects={projects} onOpen={openProject} />
      )}
    </div>
  );
}

function ProjectList({ projects, onOpen }) {
  const ready = projects.reduce((a, p) => a + p.submitted_count, 0);
  return (
    <>
      <div className="mb-8">
        <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-orange-600 font-semibold">Project Site Manager</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Pre-Audit</h1>
        <p className="text-zinc-500 mt-2 text-sm sm:text-base max-w-2xl">
          Review the site measurements your pre-audit team submitted for each BOQ line, and flag any wall that needs a correction to the client.
        </p>
        {projects.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mt-4">
            <Stat label="Ready to review" value={ready} tone="amber" />
            <Stat label="Projects" value={projects.length} tone="zinc" />
          </div>
        )}
      </div>
      {projects.length === 0 ? (
        <div className="py-24 text-center" data-testid="par-empty">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-orange-50 mb-4">
            <Sparkles className="w-7 h-7 text-orange-400" strokeWidth={1.5} />
          </div>
          <div className="font-display text-xl font-bold">Nothing here yet</div>
          <p className="text-zinc-500 mt-1 text-sm">Assign a pre-audit user to a project, and their site measurements show up here.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {projects.map((p) => (
            <button key={p.project_code} onClick={() => onOpen(p.project_code)} data-testid={`par-project-card-${p.project_code}`}
                    className="group text-left bg-white border border-zinc-200 rounded-xl p-5 shadow-sm hover:shadow-md hover:-translate-y-1 hover:border-orange-300 transition-all">
              <div className="flex items-center justify-between">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-orange-50 text-orange-600"><FolderOpen className="w-5 h-5" strokeWidth={1.5} /></div>
                <ChevronRight className="w-4 h-4 text-zinc-300 group-hover:text-orange-500 transition-colors" strokeWidth={1.5} />
              </div>
              <div className="font-display text-lg font-bold tracking-tight mt-4 truncate">{p.project_name}</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400"><ProjRef code={p.project_code} /></div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {p.submitted_count > 0 && <Pill tone="amber">{p.submitted_count} to review</Pill>}
                {p.pending_count > 0 && <Pill tone="zinc">{p.pending_count} pending</Pill>}
                <Pill tone="zinc">{p.total} wall{p.total !== 1 ? "s" : ""}</Pill>
              </div>
            </button>
          ))}
        </div>
      )}
    </>
  );
}

function ProjectView({ project, selectedBid, onSelect, onBack, onDone }) {
  const selected = project.lines.find((l) => l.boq_id === selectedBid) || project.lines[0];
  const [downloading, setDownloading] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [checked, setChecked] = useState(() => new Set());
  const [bulkPreauditId, setBulkPreauditId] = useState("");
  const [bulkReason, setBulkReason] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);
  const options = project.preaudit_options || [];

  // Reset selection when project changes or bulk mode is toggled off
  const toggleBulk = () => {
    setBulkMode((v) => {
      if (v) { setChecked(new Set()); setBulkPreauditId(""); setBulkReason(""); }
      return !v;
    });
  };
  const toggleChecked = (bid) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(bid)) next.delete(bid); else next.add(bid);
      return next;
    });
  };
  const selectAllUnassigned = () => {
    setChecked(new Set(project.lines.filter((l) => !l.preaudit_id).map((l) => l.boq_id)));
  };
  const selectAllVisible = () => {
    setChecked(new Set(project.lines.map((l) => l.boq_id)));
  };
  const clearSelection = () => setChecked(new Set());

  // Does the current selection include any wall already assigned to a
  // DIFFERENT pre-audit user? Only then a reason is required.
  const needsReason = bulkPreauditId && Array.from(checked).some((bid) => {
    const l = project.lines.find((x) => x.boq_id === bid);
    return l && l.preaudit_id && l.preaudit_id !== bulkPreauditId;
  });

  async function downloadExcel() {
    setDownloading(true);
    try {
      const r = await api.get(`/preaudit-review/${project.project_code}/export-xlsx`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      a.href = url; a.download = `site-measurements-${project.project_code}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Download failed"); }
    finally { setDownloading(false); }
  }

  async function assignPreaudit(wid, preaudit_id, currentId) {
    let reason = "";
    if (currentId && currentId !== preaudit_id) {
      reason = (window.prompt(
        preaudit_id
          ? "Pre-audit is already assigned for this wall. Give a reason to change it:"
          : "Pre-audit is already assigned for this wall. Give a reason to clear it:"
      ) || "").trim();
      if (!reason) { toast.error("A reason is required to change the pre-audit user."); return; }
    }
    try {
      await api.patch(`/projects/${project.project_code}/boq/${wid}/preaudit-assign`, { preaudit_id, reason });
      toast.success(preaudit_id ? (reason ? "Pre-audit reassigned" : "Pre-audit assigned") : "Pre-audit cleared");
      onDone();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Failed"); }
  }

  async function bulkAssign() {
    if (!bulkPreauditId) { toast.error("Pick a pre-audit user."); return; }
    if (checked.size === 0) { toast.error("Select at least one wall."); return; }
    if (needsReason && !bulkReason.trim()) {
      toast.error("Some selected walls are already assigned to a different pre-audit user. Give a reason.");
      return;
    }
    setBulkSaving(true);
    try {
      const { data } = await api.post(`/projects/${project.project_code}/walls/preaudit-bulk-assign`, {
        wall_ids: Array.from(checked),
        preaudit_id: bulkPreauditId,
        reason: bulkReason.trim(),
      });
      toast.success(`Assigned ${data.updated} wall(s) to ${data.preaudit_name}.`);
      setBulkMode(false); setChecked(new Set()); setBulkPreauditId(""); setBulkReason("");
      onDone();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Bulk assign failed"); }
    finally { setBulkSaving(false); }
  }

  return (
    <div data-testid={`par-project-view-${project.project_code}`}>
      <button onClick={onBack} data-testid="par-back-btn"
              className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-orange-600 transition-colors mb-4">
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> All projects
      </button>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-orange-600 font-semibold"><ProjRef code={project.project_code} /></div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-1">{project.project_name}</h1>
          <p className="text-zinc-500 mt-1 text-sm">Site measurements as per BOQ · {project.lines.length} wall{project.lines.length !== 1 ? "s" : ""}.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={toggleBulk} disabled={options.length === 0} data-testid="par-toggle-bulk"
                  className={`inline-flex items-center gap-2 px-4 h-11 rounded-xl border text-sm font-medium transition disabled:opacity-40 ${bulkMode ? "bg-orange-500 text-white border-orange-500" : "bg-white border-zinc-200 text-zinc-700 hover:border-orange-300"}`}>
            {bulkMode ? <><X className="w-4 h-4" strokeWidth={1.6} /> Exit bulk</> : <><CheckSquare className="w-4 h-4" strokeWidth={1.6} /> Bulk assign</>}
          </button>
          <button onClick={downloadExcel} disabled={downloading} data-testid="par-export-xlsx"
                  title="Download all site measurements & photos as Excel"
                  className="inline-flex items-center gap-2 px-4 h-11 rounded-xl bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-800 transition disabled:opacity-40">
            <Download className="w-4 h-4" strokeWidth={1.5} />
            {downloading ? "Preparing…" : "Measurements (Excel)"}
          </button>
        </div>
      </div>
      {options.length === 0 && (
        <div className="mb-5 p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm" data-testid="par-no-preaudit-team">
          No pre-audit users on this project yet. Add them first via the project&apos;s <b>Site Pre-Audit Team</b> section, then assign them to each sub-scope here.
        </div>
      )}

      {/* Msg 998 — bulk-assign action bar. Sticky so it stays visible while
          scrolling the long line list. */}
      {bulkMode && (
        <div className="mb-5 sticky top-16 z-10 bg-white border border-orange-200 rounded-xl shadow-md p-4"
             data-testid="par-bulk-bar">
          <div className="flex flex-wrap items-center gap-3">
            <div className="text-sm font-medium text-zinc-800 mr-2" data-testid="par-bulk-count">
              {checked.size} selected
            </div>
            <button onClick={selectAllUnassigned} data-testid="par-bulk-select-unassigned"
                    className="text-xs px-3 h-8 rounded-lg border border-zinc-200 hover:border-orange-300 text-zinc-700">
              Select all unassigned
            </button>
            <button onClick={selectAllVisible} data-testid="par-bulk-select-all"
                    className="text-xs px-3 h-8 rounded-lg border border-zinc-200 hover:border-orange-300 text-zinc-700">
              Select all
            </button>
            {checked.size > 0 && (
              <button onClick={clearSelection} data-testid="par-bulk-clear"
                      className="text-xs px-3 h-8 rounded-lg text-zinc-500 hover:text-zinc-800">
                Clear
              </button>
            )}
            <div className="flex-1 min-w-[200px]" />
            <select value={bulkPreauditId} onChange={(e) => setBulkPreauditId(e.target.value)}
                    data-testid="par-bulk-preaudit-select"
                    className="text-sm border border-zinc-200 rounded-lg px-2.5 h-9 bg-white text-zinc-700 focus:border-orange-500 outline-none">
              <option value="">Assign to…</option>
              {options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
            <button onClick={bulkAssign} disabled={bulkSaving || !bulkPreauditId || checked.size === 0}
                    data-testid="par-bulk-assign-btn"
                    className="inline-flex items-center gap-2 px-4 h-9 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-40">
              <UserCheck className="w-4 h-4" strokeWidth={1.6} /> {bulkSaving ? "Assigning…" : "Assign selected"}
            </button>
          </div>
          {needsReason && (
            <div className="mt-3">
              <label className="block text-[11px] font-mono uppercase tracking-widest text-rose-600 mb-1">
                Reason (required) — some selected walls are already assigned to a different pre-audit user
              </label>
              <input value={bulkReason} onChange={(e) => setBulkReason(e.target.value)}
                     placeholder="e.g. Original pre-audit user on leave — reassigning to cover"
                     data-testid="par-bulk-reason"
                     className="w-full text-sm rounded-lg border border-rose-200 px-3 h-9 focus:outline-none focus:ring-2 focus:ring-rose-300" />
            </div>
          )}
        </div>
      )}

      <div className="grid lg:grid-cols-[minmax(260px,320px)_1fr] gap-6 items-start">
        <div className="bg-white border border-zinc-200 rounded-xl divide-y divide-zinc-100 overflow-hidden lg:sticky lg:top-20 lg:max-h-[calc(100vh-6.5rem)] lg:overflow-y-auto"
             data-testid="par-wall-list">
          {project.lines.map((l) => (
            <LineRow key={l.boq_id} l={l} active={l.boq_id === selected?.boq_id}
                     onClick={() => (bulkMode ? toggleChecked(l.boq_id) : onSelect(l.boq_id))}
                     bulkMode={bulkMode} isChecked={checked.has(l.boq_id)} />
          ))}
        </div>
        {selected && !bulkMode && (
          <div className="lg:sticky lg:top-20 lg:max-h-[calc(100vh-6.5rem)] lg:overflow-y-auto rounded-2xl">
            <LineDetail key={selected.boq_id} code={project.project_code} line={selected}
                        options={options} onAssign={assignPreaudit} onDone={onDone} />
          </div>
        )}
        {bulkMode && (
          <div className="bg-white border border-zinc-200 rounded-2xl p-8 text-center text-zinc-500 text-sm"
               data-testid="par-bulk-placeholder">
            <CheckSquare className="w-8 h-8 mx-auto mb-3 text-orange-400" strokeWidth={1.4} />
            <div className="font-medium text-zinc-700 mb-1">Bulk assign mode</div>
            Tick walls on the left, pick a pre-audit user in the bar above and hit Assign.
          </div>
        )}
      </div>
    </div>
  );
}

function LineRow({ l, active, onClick, bulkMode = false, isChecked = false }) {
  const meta = STATE[lineState(l)];
  const thumb = l.photos?.[0];
  return (
    <button onClick={onClick} data-testid={`par-line-row-${l.boq_id}`}
            className={`w-full flex items-center gap-3 px-3.5 py-3 text-left transition-colors ${bulkMode && isChecked ? "bg-emerald-50/70" : (active && !bulkMode ? "bg-orange-50/70" : "hover:bg-zinc-50")}`}>
      {bulkMode ? (
        <div className={`w-6 h-6 rounded-md border flex items-center justify-center flex-shrink-0 transition ${isChecked ? "bg-emerald-600 border-emerald-600" : "bg-white border-zinc-300"}`}
             data-testid={`par-line-check-${l.boq_id}`}>
          {isChecked ? <CheckSquare className="w-4 h-4 text-white" strokeWidth={2} /> : <Square className="w-4 h-4 text-zinc-300" strokeWidth={1.6} />}
        </div>
      ) : (
        <div className="w-10 h-10 rounded-lg bg-zinc-50 border border-zinc-200 overflow-hidden flex items-center justify-center flex-shrink-0">
          {thumb && isImg(thumb) ? <img src={thumb} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} /> : <Layers className="w-4 h-4 text-zinc-300" strokeWidth={1.5} />}
        </div>
      )}
      <div className="min-w-0 flex-1">
        {l.wall_label && <div className="font-mono text-[10px] uppercase tracking-wider text-orange-500">{l.wall_label}</div>}
        <div className="text-sm font-medium text-zinc-900 truncate">{l.title}</div>
        <div className="flex items-center gap-1.5 mt-0.5">
          <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
          <span className="text-[11px] text-zinc-500">{meta.label}</span>
        </div>
        <div className="flex items-center gap-1 mt-1 text-[10px]" data-testid={`par-row-preaudit-${l.boq_id}`}>
          {l.preaudit_name
            ? <span className="inline-flex items-center gap-1 text-emerald-700"><UserCheck className="w-3 h-3" strokeWidth={1.6} /> {l.preaudit_name}</span>
            : <span className="inline-flex items-center gap-1 text-zinc-400"><UserPlus className="w-3 h-3" strokeWidth={1.6} /> No pre-audit user</span>}
        </div>
      </div>
      {!bulkMode && <ChevronRight className={`w-4 h-4 flex-shrink-0 ${active ? "text-orange-500" : "text-zinc-300"}`} strokeWidth={1.5} />}
    </button>
  );
}

function LineDetail({ code, line, options = [], onAssign, onDone }) {
  const st = lineState(line);
  const meta = STATE[st];
  const detailRef = useRef(null);
  // On small screens the detail renders below the wall list — bring it into view.
  useEffect(() => {
    if (window.innerWidth < 1024 && detailRef.current) {
      const r = detailRef.current.getBoundingClientRect();
      if (r.top < 0 || r.top > window.innerHeight * 0.5) {
        detailRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [line.boq_id]);
  const [note, setNote] = useState("");
  const [includePhoto, setIncludePhoto] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [acting, setActing] = useState(false);
  const [showChange, setShowChange] = useState(false);
  const [changeNote, setChangeNote] = useState("");
  const dim = (line.height && line.width) ? `${line.height} × ${line.width} ${line.measure_unit || ""}` : null;

  async function approve() {
    setActing(true);
    try {
      await api.post(`/projects/${code}/boq/${line.boq_id}/preaudit-approve`);
      toast.success("Pre-audit approved."); onDone();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Failed"); }
    finally { setActing(false); }
  }
  async function sendBack() {
    if (!changeNote.trim()) { toast.error("Tell the pre-audit user what to change"); return; }
    setActing(true);
    try {
      await api.post(`/projects/${code}/boq/${line.boq_id}/preaudit-reject`, { note: changeNote.trim() });
      toast.success("Sent back to the pre-audit user."); setShowChange(false); setChangeNote(""); onDone();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Failed"); }
    finally { setActing(false); }
  }

  async function send() {
    if (!note.trim()) { toast.error("Write the note for the client"); return; }
    setSaving(true);
    try {
      await api.post(`/projects/${code}/boq/${line.boq_id}/site-note`,
                     { note: note.trim(), include_preaudit_photo: includePhoto });
      toast.success("Site note sent to the client."); setShowForm(false); setNote(""); onDone();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  }

  return (
    <div ref={detailRef} className="bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden" data-testid={`par-detail-${line.boq_id}`}>
      <div className="p-5 sm:p-6 border-b border-zinc-100 flex items-start justify-between gap-4">
        <div className="min-w-0">
          {line.wall_label && <div className="font-mono text-[11px] uppercase tracking-widest text-orange-500">{line.wall_label}</div>}
          <h3 className="font-display text-xl font-bold tracking-tight mt-0.5">{line.title}</h3>
          {line.preaudit_by && <div className="text-xs text-zinc-500 mt-1">Pre-audited by {line.preaudit_by}</div>}
        </div>
        <span className={`inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full text-[11px] font-medium border whitespace-nowrap ${meta.chip}`} data-testid={`par-status-${line.boq_id}`}>
          {st === "approved" ? <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.5} />
            : st === "changes" ? <MessageSquare className="w-3.5 h-3.5" strokeWidth={1.5} />
            : <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />} {meta.label}
        </span>
      </div>

      {/* Pre-audit user assignment (per sub-scope) — locked once chosen, change needs a reason */}
      <div className="px-5 sm:px-6 py-4 border-b border-zinc-100" data-testid={`par-assign-${line.boq_id}`}>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] font-semibold text-zinc-400 mb-2 flex items-center gap-1">
          <UserCheck className="w-3.5 h-3.5" strokeWidth={1.5} /> Pre-audit user
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {line.preaudit_name && (
            <span className="inline-flex items-center gap-1.5 px-2.5 h-8 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 border border-emerald-300" data-testid={`par-assigned-name-${line.boq_id}`}>
              <Lock className="w-3 h-3" strokeWidth={1.6} /> {line.preaudit_name}
            </span>
          )}
          <select value={line.preaudit_id || ""} disabled={options.length === 0}
                  data-testid={`par-preaudit-select-${line.boq_id}`}
                  onChange={(e) => onAssign(line.boq_id, e.target.value, line.preaudit_id || "")}
                  className="text-sm border border-zinc-200 rounded-lg px-2.5 py-2 bg-white text-zinc-700 hover:border-orange-300 focus:border-orange-500 outline-none disabled:opacity-50">
            <option value="">— unassigned —</option>
            {options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
          {line.preaudit_name && <span className="text-[11px] text-zinc-400">Locked · changing needs a reason</span>}
        </div>
      </div>

      {/* Site measurement */}
      <div className="p-5 sm:p-6 border-b border-zinc-100">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] font-semibold text-zinc-400 mb-2 flex items-center gap-1">
          <Ruler className="w-3.5 h-3.5" strokeWidth={1.5} /> Site measurement (as per BOQ)
        </div>
        {line.measured ? (
          <div className="text-sm text-zinc-800">
            {dim && <span className="text-zinc-500">{dim} → </span>}
            <b>{line.total_quantity} {line.unit}</b>
            {line.is_sqm && line.total_quantity_sqft ? <span className="text-zinc-400"> (≈ {line.total_quantity_sqft} sqft)</span> : null}
          </div>
        ) : <div className="text-sm text-zinc-400 italic">Not measured yet — awaiting the pre-audit team.</div>}
        {line.comment && (
          <div className="mt-3 bg-amber-50 border border-amber-100 rounded-lg p-3" data-testid={`par-comment-${line.boq_id}`}>
            <div className="text-[10px] font-mono uppercase tracking-widest text-amber-700 mb-1 flex items-center gap-1"><MessageSquare className="w-3 h-3" strokeWidth={1.5} /> Pre-audit comment</div>
            <p className="text-sm text-zinc-800">{line.comment}</p>
          </div>
        )}
      </div>

      {/* Photos */}
      {line.photos?.length > 0 && (
        <div className="p-5 sm:p-6 border-b border-zinc-100">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] font-semibold text-zinc-400 mb-2 flex items-center gap-1">
            <ImageIcon className="w-3.5 h-3.5" strokeWidth={1.5} /> Site photos
          </div>
          <div className="flex flex-wrap gap-2">
            {line.photos.map((p, i) => (
              <a key={i} href={p} target="_blank" rel="noreferrer" data-testid={`par-photo-${line.boq_id}-${i}`}
                 className="block w-24 h-20 rounded-lg overflow-hidden border border-zinc-200 hover:ring-2 hover:ring-orange-300 transition">
                <img src={p} alt="site" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Existing client note */}
      {line.site_client_note && (
        <div className="px-5 sm:px-6 py-4 bg-emerald-50/50 border-b border-emerald-100" data-testid={`par-sent-note-${line.boq_id}`}>
          <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-700 mb-1 flex items-center gap-1"><Lock className="w-3 h-3" strokeWidth={1.5} /> Sent to client</div>
          <p className="text-sm text-zinc-800">{line.site_client_note}</p>
          {line.site_client_note_photo && (
            <a href={line.site_client_note_photo} target="_blank" rel="noreferrer" className="mt-2 inline-block w-20 h-16 rounded-lg overflow-hidden border border-emerald-200">
              <img src={line.site_client_note_photo} alt="note" className="w-full h-full object-cover" />
            </a>
          )}
          <div className="text-[11px] text-zinc-400 mt-1">Status: {line.site_client_note_status || "pending"}</div>
        </div>
      )}

      {/* PSM approval — confirm what the pre-audit user submitted */}
      {st !== "pending" && (
        <div className="px-5 sm:px-6 py-4 border-b border-zinc-100" data-testid={`par-approval-${line.boq_id}`}>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] font-semibold text-zinc-400 mb-2 flex items-center gap-1">
            <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.5} /> PSM approval
          </div>
          {st === "approved" ? (
            <EditApproved code={code} line={line} onDone={onDone} />
          ) : st === "changes" ? (
            <div className="text-sm" data-testid={`par-changes-${line.boq_id}`}>
              <div className="text-rose-700 flex items-center gap-1.5"><MessageSquare className="w-4 h-4" strokeWidth={1.5} /> Sent back to the pre-audit user:</div>
              {line.preaudit_review_note && <p className="text-zinc-700 mt-1 pl-5">“{line.preaudit_review_note}”</p>}
              <p className="text-[11px] text-zinc-400 mt-1 pl-5">Waiting for them to re-submit.</p>
            </div>
          ) : !showChange ? (
            <div className="flex gap-2">
              <button onClick={approve} disabled={acting} data-testid={`par-approve-${line.boq_id}`}
                      className="flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-emerald-600 text-white font-medium hover:bg-emerald-700 disabled:opacity-50">
                <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} /> {acting ? "…" : "Approve pre-audit"}
              </button>
              <button onClick={() => setShowChange(true)} disabled={acting} data-testid={`par-open-change-${line.boq_id}`}
                      className="inline-flex items-center justify-center gap-2 h-11 px-4 rounded-xl border border-rose-200 text-rose-700 font-medium hover:bg-rose-50 disabled:opacity-50">
                Send back
              </button>
            </div>
          ) : (
            <div className="space-y-2" data-testid={`par-change-form-${line.boq_id}`}>
              <textarea value={changeNote} onChange={(e) => setChangeNote(e.target.value)} rows={2}
                        placeholder="What needs correcting? e.g. Re-measure the height — looks off."
                        className="w-full text-sm rounded-lg border border-zinc-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-rose-300"
                        data-testid={`par-change-input-${line.boq_id}`} />
              <div className="flex gap-2">
                <button onClick={sendBack} disabled={acting || !changeNote.trim()} data-testid={`par-send-back-${line.boq_id}`}
                        className="flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-rose-600 text-white font-medium hover:bg-rose-700 disabled:opacity-50">
                  {acting ? "Sending…" : "Send back for changes"}
                </button>
                <button onClick={() => { setShowChange(false); setChangeNote(""); }} className="px-4 text-sm text-zinc-500 hover:text-zinc-800">Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Action — send a correction note to the client */}
      <div className="p-5 sm:p-6">
        {!showForm ? (
          <button onClick={() => setShowForm(true)} disabled={!line.measured} data-testid={`par-open-note-${line.boq_id}`}
                  className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-xl bg-orange-500 text-white font-medium shadow-sm hover:bg-orange-600 transition-all hover:-translate-y-0.5 disabled:opacity-40 disabled:hover:translate-y-0">
            <Send className="w-4 h-4" strokeWidth={1.5} /> {line.site_client_note ? "Send another note to client" : "Send note to client"}
          </button>
        ) : (
          <div className="space-y-3" data-testid={`par-note-form-${line.boq_id}`}>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
                      placeholder="e.g. This wall surface needs levelling before installation. Please confirm."
                      className="w-full text-sm rounded-lg border border-zinc-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-300"
                      data-testid={`par-note-input-${line.boq_id}`} />
            {line.photos?.length > 0 && (
              <label className="flex items-center gap-2 text-sm text-zinc-600">
                <input type="checkbox" checked={includePhoto} onChange={(e) => setIncludePhoto(e.target.checked)} data-testid={`par-include-photo-${line.boq_id}`} />
                Include the pre-audit photo
              </label>
            )}
            <div className="flex gap-2">
              <button onClick={send} disabled={saving || !note.trim()} data-testid={`par-send-${line.boq_id}`}
                      className="flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-orange-500 text-white font-medium hover:bg-orange-600 disabled:opacity-50">
                <Send className="w-4 h-4" strokeWidth={1.5} /> {saving ? "Sending…" : "Send to client"}
              </button>
              <button onClick={() => setShowForm(false)} className="px-4 text-sm text-zinc-500 hover:text-zinc-800">Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const cls = { amber: "bg-amber-50 text-amber-800 border-amber-200", zinc: "bg-zinc-100 text-zinc-700 border-zinc-200" }[tone];
  return <span className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-xs font-medium border ${cls}`}><span className="font-bold tabular-nums">{value}</span> {label}</span>;
}
function Pill({ tone, children }) {
  const cls = { amber: "bg-amber-100 text-amber-800", zinc: "bg-zinc-100 text-zinc-600" }[tone];
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${cls}`}>{children}</span>;
}


/* Msg 998 — approved measurements can still be edited by the PSM with a reason.
   Shown as a compact "Approved" chip + an "Edit measurement" toggle that opens
   an inline form (mirrors the pre-audit user's own form so it feels familiar). */
function EditApproved({ code, line, onDone }) {
  const isArea = !!line.is_area;
  const [open, setOpen] = useState(false);
  const [h, setH] = useState(line.height ?? "");
  const [w, setW] = useState(line.width ?? "");
  const [u, setU] = useState(line.measure_unit || "in");
  const [qty, setQty] = useState(line.total_quantity ?? "");
  const [photos, setPhotos] = useState(line.photos || []);
  const [comment, setComment] = useState(line.comment || "");
  const [newPhoto, setNewPhoto] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);

  function addPhoto(url) { if (url) { setPhotos((p) => [...p, url]); setNewPhoto(""); } }

  async function save() {
    if (photoBusy) { toast.error("Wait — a site photo is still uploading."); return; }
    if (photos.length === 0) { toast.error("A site photo is required. Keep or add at least one photo."); return; }
    if (!reason.trim()) { toast.error("Give a reason for the edit."); return; }
    if (isArea && (!h || !w)) { toast.error("Enter length and breadth"); return; }
    if (!isArea && !qty) { toast.error("Enter quantity"); return; }
    setSaving(true);
    try {
      const body = isArea
        ? { height: parseFloat(h), width: parseFloat(w), unit: u, photos, comment: comment.trim(), reason: reason.trim() }
        : { quantity: parseFloat(qty), photos, comment: comment.trim(), reason: reason.trim() };
      const r = await api.post(`/projects/${code}/boq/${line.boq_id}/preaudit-edit`, body);
      toast.success(`Updated: ${r.data.measured} ${line.unit || ""}`);
      setOpen(false); setReason("");
      onDone();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Edit failed"); }
    finally { setSaving(false); }
  }

  return (
    <div data-testid={`par-approved-${line.boq_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="text-sm text-emerald-700 flex items-center gap-1.5">
          <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} />
          Approved{line.preaudit_approved_by ? ` by ${line.preaudit_approved_by}` : ""}.
        </div>
        {!open && (
          <button onClick={() => setOpen(true)} data-testid={`par-edit-approved-${line.boq_id}`}
                  className="inline-flex items-center gap-1.5 text-xs font-medium px-3 h-8 rounded-lg border border-zinc-200 text-zinc-700 hover:border-orange-300 hover:text-orange-600 transition">
            <Pencil className="w-3.5 h-3.5" strokeWidth={1.6} /> Edit measurement
          </button>
        )}
      </div>

      {line.preaudit_edited_after_approval && line.preaudit_last_edit_reason && (
        <div className="mt-2 p-2.5 rounded-lg bg-amber-50 border border-amber-100 text-xs" data-testid={`par-edit-history-${line.boq_id}`}>
          <div className="text-[10px] font-mono uppercase tracking-widest text-amber-700 mb-0.5">Last edited after approval</div>
          <div className="text-zinc-700">
            {line.preaudit_last_edit_by && <b>{line.preaudit_last_edit_by}</b>}
            {line.preaudit_last_edit_reason && <>: “{line.preaudit_last_edit_reason}”</>}
          </div>
        </div>
      )}

      {open && (
        <div className="mt-4 p-4 rounded-xl bg-zinc-50 border border-zinc-200 space-y-4" data-testid={`par-edit-form-${line.boq_id}`}>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] font-semibold text-zinc-400 mb-2 flex items-center gap-1">
              <Ruler className="w-3.5 h-3.5" strokeWidth={1.5} /> Measurement
            </div>
            {isArea ? (
              <div className="grid grid-cols-3 gap-3">
                <EditField label="Length" v={h} set={setH} tid={`par-edit-length-${line.boq_id}`} />
                <EditField label="Breadth" v={w} set={setW} tid={`par-edit-breadth-${line.boq_id}`} />
                <div>
                  <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">Unit</label>
                  <select value={u} onChange={(e) => setU(e.target.value)} data-testid={`par-edit-unit-${line.boq_id}`}
                          className="mt-1 w-full h-10 rounded-lg border border-zinc-300 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300">
                    <option value="ft">ft</option><option value="in">in</option>
                    <option value="cm">cm</option><option value="m">m</option>
                  </select>
                </div>
              </div>
            ) : (
              <EditField label={`Quantity (${line.unit || ""})`} v={qty} set={setQty} tid={`par-edit-qty-${line.boq_id}`} />
            )}
          </div>

          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] font-semibold text-zinc-400 mb-2 flex items-center gap-1">
              <ImageIcon className="w-3.5 h-3.5" strokeWidth={1.5} /> Site photos
            </div>
            {photos.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {photos.map((p, i) => (
                  <div key={i} className="relative w-20 h-16 rounded-lg overflow-hidden border border-zinc-200 group">
                    <img src={p} alt="site" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                    <button onClick={() => setPhotos((ph) => ph.filter((_, j) => j !== i))}
                            data-testid={`par-edit-photo-remove-${line.boq_id}-${i}`}
                            className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition">
                      <X className="w-3 h-3" strokeWidth={2} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <FileUpload value={newPhoto} onChange={addPhoto} onBusyChange={setPhotoBusy} label="Add a site photo"
                        accept=".png,.jpg,.jpeg,.webp" testid={`par-edit-photo-${line.boq_id}`} />
          </div>

          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] font-semibold text-zinc-400 mb-2 flex items-center gap-1">
              <MessageSquare className="w-3.5 h-3.5" strokeWidth={1.5} /> Condition comment
            </div>
            <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2}
                      className="w-full text-sm rounded-lg border border-zinc-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-300"
                      data-testid={`par-edit-comment-${line.boq_id}`} />
          </div>

          <div>
            <label className="block text-[10px] font-mono uppercase tracking-[0.2em] font-semibold text-rose-600 mb-1">
              Reason for edit (required)
            </label>
            <input value={reason} onChange={(e) => setReason(e.target.value)}
                   placeholder="e.g. Re-measured on site, wall was wider than first noted"
                   data-testid={`par-edit-reason-${line.boq_id}`}
                   className="w-full text-sm rounded-lg border border-rose-200 px-3 h-10 focus:outline-none focus:ring-2 focus:ring-rose-300" />
          </div>

          <div className="flex gap-2">
            <button onClick={save} disabled={saving || photoBusy || photos.length === 0 || !reason.trim()}
                    data-testid={`par-edit-save-${line.boq_id}`}
                    className="flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-orange-600 text-white font-medium hover:bg-orange-700 disabled:opacity-40">
              <Pencil className="w-4 h-4" strokeWidth={1.5} /> {saving ? "Saving…" : "Save with reason"}
            </button>
            <button onClick={() => setOpen(false)} className="px-4 text-sm text-zinc-500 hover:text-zinc-800">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function EditField({ label, v, set, tid }) {
  return (
    <div>
      <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">{label}</label>
      <input type="number" step="0.01" value={v} onChange={(e) => set(e.target.value)} data-testid={tid}
             className="mt-1 w-full h-10 rounded-lg border border-zinc-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300" />
    </div>
  );
}
